/**
 * The one decision function (docs/03 "The decision function", ADR-0011). Pure; run by the
 * API's PolicyGuard on every route and by the SPA's useCan() for every nav item and button.
 *
 *   0. Impersonation  money-moving permissions → Deny IMPERSONATION_RESTRICTED
 *   1. Platform       super_admin → Allow (platform action)
 *                     other staff → Allow if the platform permission is in their set, or
 *                                   the permission is a read and they hold platform:read;
 *                                   otherwise fall through to the org checks
 *   2. Org role       no org → ORG_REQUIRED; suspended → MEMBERSHIP_INACTIVE;
 *                     permission not in role map ∪ grants → ROLE_FORBIDDEN          (403)
 *   3. Entitlement    feature not allowed by the snapshot → ENTITLEMENT_MISSING    (402)
 *   4. Resource       resource policy false → RESOURCE_FORBIDDEN (404, no existence leak)
 */
import type { Actor } from './actor.js';
import type { EntitlementDenyReason } from './entitlements.js';
import { isPermission, isReadPermission, type Permission } from './permissions/index.js';
import { isPlatformPermission, PLATFORM_ROLE_PERMISSIONS, type PlatformPermission } from './platform.js';
import type { ResourceCheck } from './resource.js';
import { IMPERSONATION_DENIED_PERMISSIONS } from './roles.js';

export type DenyCode =
  | 'IMPERSONATION_RESTRICTED'
  | 'ORG_REQUIRED'
  | 'MEMBERSHIP_INACTIVE'
  | 'ROLE_FORBIDDEN'
  | 'ENTITLEMENT_MISSING'
  | 'RESOURCE_FORBIDDEN';

export type Allow = {
  allowed: true;
  /** True when granted by the platform step; the audit log records it as a platform action. */
  platformAction: boolean;
};

export type Deny = {
  allowed: false;
  code: DenyCode;
  permission: Permission | PlatformPermission;
  /** Set for ENTITLEMENT_MISSING (the 402 upsell payload needs it). */
  feature?: string;
  reason?: EntitlementDenyReason;
};

export type Decision = Allow | Deny;

export type AuthorizeRequest<R = unknown> = {
  permission: Permission | PlatformPermission;
  feature?: string;
  resource?: ResourceCheck<R>;
};

/** HTTP status for each deny code (docs/03 "Error contract", docs/08). */
export const DENY_STATUS: Record<DenyCode, number> = {
  IMPERSONATION_RESTRICTED: 403,
  ORG_REQUIRED: 400,
  MEMBERSHIP_INACTIVE: 403,
  ROLE_FORBIDDEN: 403,
  ENTITLEMENT_MISSING: 402,
  RESOURCE_FORBIDDEN: 404,
};

const IMPERSONATION_DENIED: ReadonlySet<string> = new Set(IMPERSONATION_DENIED_PERMISSIONS);

const allow = (platformAction = false): Allow => ({ allowed: true, platformAction });
const deny = (
  code: DenyCode,
  permission: Permission | PlatformPermission,
  extra: Partial<Deny> = {},
): Deny => ({
  allowed: false,
  code,
  permission,
  ...extra,
});

export function authorize<R>(actor: Actor, request: AuthorizeRequest<R>): Decision {
  const { permission, feature, resource } = request;
  if (!isPermission(permission) && !isPlatformPermission(permission)) {
    throw new RangeError(`Unknown permission "${String(permission)}"`);
  }

  // 0. Impersonation never moves money, whoever the target is.
  if (actor.impersonatedBy && IMPERSONATION_DENIED.has(permission)) {
    return deny('IMPERSONATION_RESTRICTED', permission);
  }

  // 1. Platform short-circuit.
  if (actor.platformRole) {
    if (actor.platformRole === 'super_admin') return allow(true);
    const staffPermissions = PLATFORM_ROLE_PERMISSIONS[actor.platformRole];
    if (isPlatformPermission(permission) && staffPermissions.has(permission)) return allow(true);
    if (isPermission(permission) && isReadPermission(permission) && staffPermissions.has('platform:read')) {
      return allow(true);
    }
    // otherwise fall through to the org checks (staff may also be members of an org)
  }

  // 2. Org role.
  if (isPlatformPermission(permission)) return deny('ROLE_FORBIDDEN', permission);
  const org = actor.org;
  if (!org) return deny('ORG_REQUIRED', permission);
  if (org.membershipStatus === 'suspended') return deny('MEMBERSHIP_INACTIVE', permission);
  if (!org.permissions.has(permission)) return deny('ROLE_FORBIDDEN', permission);

  // 3. Entitlement.
  if (feature !== undefined && !org.entitlements.allows(feature)) {
    return deny('ENTITLEMENT_MISSING', permission, {
      feature,
      reason: org.entitlements.denyReason(feature) ?? 'not_in_plan',
    });
  }

  // 4. Resource.
  if (resource && !resource.policy(actor, resource.value, permission)) {
    return deny('RESOURCE_FORBIDDEN', permission);
  }

  return allow(false);
}
